import React, { useState, useEffect } from "react";
import "../css/Login.css";
import api from "../api";
import { useNavigate, Link } from "react-router-dom";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";
import { resetSidebarState, setSelectedMenu } from "../redux/sidebarSlice";
import { useDispatch } from "react-redux";
import { branding } from "../utils/branding"; // adjust path if needed
import CompanyLogo from "../assets/Images/CompName.svg";
import VelaKoda from "../assets/Images/LoginLogo.svg";
import VelaKodaMobile from "../assets/Images/MobileLogo.svg";
import refreshIcon from "../assets/icons/refreshIcon.png";

const images = [
  // "/img/superadminbg1.png",
  "/img/superadminbg2.png",
  "/img/superadminbg5.png",
  "/img/superadminbg3.png",
  "/img/superadminbg4.png",
  "/img/superadminbg5.png",
];

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentImage, setCurrentImage] = useState(0);

  const [email, setEmail] = useState("");
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [termsConditions, setTermsConditions] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState(null);
  const [termsError, setTermsError] = useState(true);

  const [emailError, setEmailError] = useState("");
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaValue, setCaptchaValue] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const [captchaImage, setCaptchaImage] = useState(""); // Add this state
  const [sessionId, setSessionId] = useState(null);

  // console.log("captchaRequired", captchaRequired);
  // console.log("captchaValue", captchaValue);
  // console.log("captchaError", captchaError);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (captchaRequired && !captchaImage) {
      refreshCaptcha();
    }
  }, [captchaRequired, captchaImage]);

  // ✅ Get stored user safely
  const getStoredUser = () => {
    try {
      return (
        JSON.parse(localStorage.getItem("user")) ||
        JSON.parse(sessionStorage.getItem("user"))
      );
    } catch {
      return null;
    }
  };

  const getStoredToken = () => {
    return (
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token")
    );
  };

  // Auto-login & handle must_reset_password
  useEffect(() => {
    const storedUser = getStoredUser();
    const storedToken = getStoredToken();
    const storedRole =
      localStorage.getItem("role_name") || sessionStorage.getItem("role_name");

    if (storedUser && storedToken && storedRole) {
      setLoginData({
        access_token: storedToken,
        refresh_token:
          localStorage.getItem("refresh_token") ||
          sessionStorage.getItem("refresh_token"),
        user: storedUser,
        session_id:
          localStorage.getItem("session_id") ||
          sessionStorage.getItem("session_id"),
      });

      if (storedUser.termsAccepted) setTermsConditions(true);

      if (storedUser.must_reset_password) {
        // ❌ Do NOT reuse old tokens
        localStorage.clear();
        sessionStorage.clear();
        showErrorToast(
          "You must reset your password before accessing the system.",
        );
        navigate("/reset-password", { replace: true });
      } else {
        proceedToDashboard(storedRole, true);
      }
    }
  }, []);

  // ✅ Handle storage changes across multiple tabs
  useEffect(() => {
    const handleStorageChange = () => {
      const storedUser = getStoredUser();
      const storedToken = getStoredToken();
      const storedRole =
        localStorage.getItem("role_name") ||
        sessionStorage.getItem("role_name");

      if (storedUser && storedToken && storedRole) {
        setLoginData({
          access_token: storedToken,
          refresh_token:
            localStorage.getItem("refresh_token") ||
            sessionStorage.getItem("refresh_token"),
          user: storedUser,
          session_id:
            localStorage.getItem("session_id") ||
            sessionStorage.getItem("session_id"),
        });

        if (storedUser.termsAccepted) setTermsConditions(true);
      } else {
        // ✅ Logout in all tabs
        setLoginData(null);
        navigate("/");
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [navigate]);

  const isValidEmail = (email) => {
    // Stricter email regex - must include valid domain and extension (e.g., .com, .in, etc.)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.trim());
  };

  // In your handleLogin or captcha check, fetch the CAPTCHA image:
  const refreshCaptcha = async (e) => {
    if (e) e.preventDefault();
    try {
      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/auth/auth-captcha`,
        { silent: true },
      );

      console.log(response.data); // ✅ object

      setCaptchaImage(response.data.captcha_svg);
      setSessionId(response.data.session_id);
    } catch (err) {
      console.error("Error fetching captcha:", err);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;

    // ✅ Terms & conditions check
    if (!termsConditions) {
      setTermsError(true);
      return;
    }

    // ✅ Username/email check
    if (!login.trim()) {
      showErrorToast("Please enter username or email.");
      return;
    }

    setLoading(true);

    if (captchaRequired && !captchaValue.trim()) {
      showErrorToast("Captcha is required. Please enter the captcha value.");
      setLoading(false);
      return;
    }

    try {
      // 🔹 Build payload
      const payload = {
        login: login.trim(),
        password,
      };

      // 🔹 Send captcha only if required
      if (captchaRequired) {
        payload.captcha = captchaValue.trim();
      }

      // 🔹 Call login API
      const response = await api.post(
        `${process.env.REACT_APP_API_URL}/auth/login`,
        payload,
        {
          silent: true, // prevents global toast/redirect
          headers: {
            "x-session-id": sessionId, // captcha session id
          },
        },
      );

      const {
        access_token,
        refresh_token,
        user,
        session_id,
        role_name,
        must_reset_password,
        reset_token,
      } = response.data;

      // 🔐 Must reset password
      if (must_reset_password) {
        sessionStorage.setItem("resetToken", reset_token);
        showErrorToast(
          "You must reset your password before accessing the system.",
        );
        navigate("/reset-password", { replace: true });
        return;
      }

      const updatedUser = { ...user, role_name, must_reset_password };

      // 🔹 Update login state
      setLoginData({
        access_token,
        refresh_token,
        session_id,
        user: updatedUser,
      });

      // 🔹 Persist tokens in storage
      [localStorage, sessionStorage].forEach((storage) => {
        storage.setItem("access_token", access_token);
        storage.setItem("refresh_token", refresh_token);
        storage.setItem("user", JSON.stringify(updatedUser));
        storage.setItem("session_id", session_id);
        storage.setItem("role_name", role_name);
      });

      // 🔹 Reset captcha state on SUCCESS
      setCaptchaRequired(false);
      setCaptchaValue("");
      setCaptchaError("");
      setCaptchaImage(null);

      proceedToDashboard(role_name, false);
    } catch (err) {
      const data = err.response?.data;

      /* ---------------------------------------------------
       ❌ CASE 1: INVALID CREDENTIALS (TOP PRIORITY)
    --------------------------------------------------- */
      if (data?.code === "INVALID_CREDENTIALS") {
        showErrorToast("Invalid username or password");

        // 🔹 Show captcha if backend says it's required
        if (data?.captcha_required) {
          setCaptchaRequired(true);
        }

        // 🔹 Clear sensitive fields
        setLogin("");
        setPassword("");
        setCaptchaValue("");

        // 🔹 Clear storage safely
        localStorage.clear();
        sessionStorage.clear();
        return;
      }

      /* ---------------------------------------------------
       ❗ CASE 2: CAPTCHA REQUIRED (no credentials error)
    --------------------------------------------------- */
      if (data?.captcha_required) {
        setCaptchaRequired(true);
        showErrorToast("Invalid captcha. Please try again.");

        setCaptchaValue("");

        if (!captchaImage || data?.refresh_captcha) {
          await refreshCaptcha();
        }
        return;
      }

      /* ---------------------------------------------------
       ❌ CASE 3: FALLBACK (unknown error)
    --------------------------------------------------- */
      localStorage.clear();
      sessionStorage.clear();
      showErrorToast(data?.message || TOAST_MESSAGES.AUTH.LOGIN_FAILED);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (label) => {
    dispatch(setSelectedMenu(label));
  };

  // ✅ Role-based dashboard navigation
  const proceedToDashboard = (roleName, isAutoLogin = false) => {
    try {
      if (!isAutoLogin) {
        showSuccessToast(TOAST_MESSAGES.AUTH.LOGIN_SUCCESS);
      }

      dispatch(resetSidebarState());

      switch (roleName?.toLowerCase()) {
        case "superadmin":
          navigate("/superadmin/dashboard", { replace: true });
          break;
        case "admin":
          navigate("/admin/dashboard", { replace: true });
          break;
        case "user":
          navigate("/user/new", { replace: true });
          handleMenuClick("New Chat");

          break;
        default:
          navigate("/", { replace: true });
        // showErrorToast(TOAST_MESSAGES.AUTH.NO_ROLE);
      }
    } catch (error) {
      localStorage.clear();
      sessionStorage.clear();
      navigate("/", { replace: true });
    }
  };

  return (
    <div className="login-container">
      <div className="login-left">
        {images.map((img, index) => (
          <div
            key={index}
            className={`bg-slide ${index === currentImage ? "active" : ""}`}
            style={{ backgroundImage: `url(${img})` }}
          >
            {/* Desktop Logo placed on the image */}
            <img src={VelaKoda} alt="logo" className="slide-logo" />
          </div>
        ))}
      </div>

      {/* Mobile Logo Only */}
      <img src={VelaKodaMobile} alt="logo" className="mobile-logo profile-i" />

      <div className="login-right d-flex flex-column align-items-start justify-content-center">
        <div className="logo-wrapper">
          <img className="zerobox-logo" src={CompanyLogo} alt="Logo" />
        </div>

        <form
          className="login-form p-4 rounded-3 mx-auto"
          onSubmit={handleLogin}
          style={{ maxWidth: "420px", width: "100%" }}
        >
          <h3 className="mb-3 mt-2 refresh-prompts text-center fw-bolder">
            Welcome Back
          </h3>
          <p className="text-center login-p mx-3 refresh-prompts">
            Enter your email address and password to login.
          </p>

          {/* Email Input */}
          <div className="w-100 mb-3">
            <div className="rounded input-group custom-search-group">
              <span className="input-group-text">
                <i className="bi bi-person-badge-fill"></i>
              </span>
              {/* <input
                type="email"
                className="form-control"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              /> */}
              <input
                type="text"
                className="form-control"
                placeholder="Username or Email"
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                required
              />
            </div>
            {/* {emailError && (
              <p className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
                {emailError}
              </p>
            )} */}
          </div>

          {/* Password Input */}
          <div className="rounded w-100 mb-2">
            <div className="input-group custom-search-group">
              <span className="input-group-text">
                <i className="bi bi-lock-fill"></i>
              </span>
              <input
                type={showPassword ? "text" : "password"}
                className="form-control"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                style={{ position: "relative", zIndex: 1 }}
              />
              <span
                className="input-group-text"
                style={{ cursor: "pointer", position: "relative", zIndex: 2 }}
                onClick={() => setShowPassword(!showPassword)}
              >
                <i
                  className={`bi ${showPassword ? "bi-eye" : "bi-eye-slash"}`}
                ></i>
              </span>
            </div>
          </div>

          {/* Remember Me & Forgot Password */}
          <div className="mb-2 d-flex justify-content-between align-items-center">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span
                className="form-check-label refresh-prompts"
                htmlFor="rememberMe"
                style={{ fontSize: "0.8rem" }}
              >
                Remember me
              </span>
            </div>
            <Link
              to="/forgot-password"
              className="text-decoration-none small"
              style={{ fontSize: "0.8rem" }}
            >
              Forgot password?
            </Link>
          </div>

          {/* Terms & Conditions */}
          {/* <div className="form-check mb-2 d-flex align-items-start gap-2">
            <input
              type="checkbox"
              className={`form-check-input mt-1 ${
                termsError ? "border-danger" : ""
              }`}
              id="termsConditions"
              checked={termsConditions}
              onChange={(e) => {
                setTermsConditions(e.target.checked);
                setTermsError(false);
                e.target.setCustomValidity(""); // clear message when checked
              }}
              onInvalid={(e) => {
                e.target.setCustomValidity(
                  "Please accept the Terms and Usage Policy to continue."
                );
              }}
              onInput={(e) => {
                e.target.setCustomValidity(""); // reset message on re-input
              }}
              required
            />
            <span
              className="form-check-label refresh-prompts"
              htmlFor="termsConditions"
              style={{ fontSize: "0.8rem", marginTop: "2px" }}
            >
              {branding("I agree to ZeroBox Consumer ")}
              <Link to="/terms" className="text-decoration-none fw-bold">
                Terms and Usage Policy
              </Link>
              .
            </span>
          </div> */}
          {captchaRequired && (
            <div className="">
              <div className="d-flex gap-2 align-items-center">
                <div className="captcha-wrapper">
                  <div
                    className="captcha-svg"
                    dangerouslySetInnerHTML={{ __html: captchaImage }}
                  />
                </div>

                {/* <img
                  src={refreshIcon}
                  type="button"
                  className="btn btn-link"
                  onClick={refreshCaptcha}
                /> */}
                <button
                  type="button"
                  className="btn btn-link p-0"
                  onClick={refreshCaptcha}
                >
                  <img src={refreshIcon} alt="Refresh captcha" />
                </button>
              </div>

              {/* {captchaError && <p className="error-text">{captchaError}</p>} */}

              <input
                type="text"
                className="form-control my-2"
                placeholder="Enter captcha"
                value={captchaValue}
                onChange={(e) => setCaptchaValue(e.target.value)}
              />
            </div>
          )}

          {/* With the Help of the Vidya Madan this captcha is done */}

          {/* Login Button */}
          <button type="submit" className="custom-btn w-100" disabled={loading}>
            {loading ? "Logging In..." : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
